import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import {
  Box,
  Button,
  Divider,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import {
  addContactNote,
  deleteContactNote,
  editContactNote,
  listContactNotes,
} from '../../api/business/endpoints';
import type { ContactNote } from '../../api/business/types';
import { useAppMutation } from '../../api/useAppMutation';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { ErrorNotice } from '../../components/ErrorNotice';
import { formatDateTime } from '../../components/format';
import { FormDialog } from '../../components/FormDialog';
import { useLanguage } from '../../i18n/LanguageContext';

const AFFECTED: Array<string> = ['contact-notes', 'contact-history', 'contacts'];

/** A contact's timeline (R0039 D-08): dated, signed notes, newest first — added, edited and deleted in place. */
export const ContactNotes = ({
  companyId,
  contactId,
  canManage,
}: {
  companyId: string;
  contactId: string;
  canManage: boolean;
}) => {
  const { language, t } = useLanguage();
  const [body, setBody] = useState('');
  const [editing, setEditing] = useState<ContactNote | null>(null);
  const [editedBody, setEditedBody] = useState('');
  const [deleting, setDeleting] = useState<ContactNote | null>(null);
  const notes = useQuery({
    queryKey: ['contact-notes', companyId, contactId],
    queryFn: () => listContactNotes(companyId, contactId),
  });

  const add = useAppMutation(
    (text: string) => addContactNote(companyId, contactId, text),
    AFFECTED,
    () => setBody(''),
  );
  const edit = useAppMutation(
    (note: ContactNote) => editContactNote(companyId, contactId, note.id, editedBody),
    AFFECTED,
    () => setEditing(null),
  );
  const remove = useAppMutation(
    (note: ContactNote) => deleteContactNote(companyId, contactId, note.id),
    AFFECTED,
    () => setDeleting(null),
  );

  return (
    <Paper variant="outlined" sx={{ p: 2.5 }}>
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
        {t('contact.section.notes')}
      </Typography>
      <ErrorNotice error={notes.error ?? add.error} onRetry={() => void notes.refetch()} />
      {canManage ? (
        <Stack
          component="form"
          spacing={1}
          sx={{ mb: 2 }}
          onSubmit={(event) => {
            event.preventDefault();
            add.mutate(body);
          }}
        >
          <TextField
            multiline
            minRows={2}
            placeholder={t('contactNotes.placeholder')}
            value={body}
            onChange={(event) => setBody(event.target.value)}
            inputProps={{ maxLength: 20000 }}
          />
          <Box>
            <Button
              type="submit"
              variant="contained"
              size="small"
              disabled={body.trim() === '' || add.isPending}
            >
              {t('contactNotes.add')}
            </Button>
          </Box>
        </Stack>
      ) : null}
      {notes.data && notes.data.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t('contactNotes.empty')}
        </Typography>
      ) : null}
      <Stack divider={<Divider flexItem />} spacing={1.5}>
        {(notes.data ?? []).map((note) => (
          <Box key={note.id}>
            <Stack direction="row" alignItems="flex-start" spacing={1}>
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography variant="caption" color="text.secondary" component="div">
                  {formatDateTime(note.createdAt, language)} · {note.authorEmail ?? t('contact.someone')}
                  {note.updatedAt !== note.createdAt
                    ? ` · ${t('contactNotes.edited', { date: formatDateTime(note.updatedAt, language) })}`
                    : ''}
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                  {note.body}
                </Typography>
              </Box>
              {canManage ? (
                <Stack direction="row">
                  <Tooltip title={t('common.edit')}>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setEditedBody(note.body);
                        setEditing(note);
                      }}
                    >
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={t('contactNotes.deleteTitle')}>
                    <IconButton size="small" onClick={() => setDeleting(note)}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              ) : null}
            </Stack>
          </Box>
        ))}
      </Stack>
      {editing !== null ? (
        <FormDialog
          title={t('common.edit')}
          onClose={() => setEditing(null)}
          onSubmit={() => edit.mutate(editing)}
          submitLabel={t('common.save')}
          isBusy={edit.isPending || editedBody.trim() === ''}
          error={edit.error}
        >
          <TextField
            autoFocus
            multiline
            minRows={4}
            value={editedBody}
            onChange={(event) => setEditedBody(event.target.value)}
            inputProps={{ maxLength: 20000 }}
          />
        </FormDialog>
      ) : null}
      {deleting !== null ? (
        <ConfirmDialog
          title={t('contactNotes.deleteTitle')}
          message={t('contactNotes.deleteMessage')}
          confirmLabel={t('contact.delete')}
          onConfirm={() => remove.mutate(deleting)}
          onClose={() => setDeleting(null)}
          isBusy={remove.isPending}
          error={remove.error}
        />
      ) : null}
    </Paper>
  );
};
